import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
'../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organisation: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Store in browser's localStorage
    const existingEnquiries = JSON.parse(localStorage.getItem('primalResponseEnquiries') || '[]');
    const newEnquiry = {
      ...formData,
      timestamp: new Date().toISOString(),
      id: Date.now()
    };
    existingEnquiries.push(newEnquiry);
    localStorage.setItem('primalResponseEnquiries', JSON.stringify(existingEnquiries));

    toast.success('Thank you for your enquiry. We will be in touch shortly.');

    // Reset form
    setFormData({
      name: '',
      organisation: '',
      email: '',
      phone: '',
      location: '',
      message: ''
    });
  };

  return (
    <div className="primal-response-site">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1c1b]/95 backdrop-blur-sm border-b border-[#3f4816]/50">
        <div className="container mx-auto px-5 md:px-10">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity">

              <img
                src="https://customer-assets.emergentagent.com/job_survival-edge/artifacts/1x9m5on7_logo.png"
                alt="Primal Response Logo"
                className="h-12 w-auto" />

              <span className="text-[#d9fb06] font-bold text-xl md:text-2xl tracking-tight hidden sm:inline">
                PRIMAL RESPONSE
              </span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('why')} className="nav-link text-sm">Why Primal Response</button>
              <button onClick={() => scrollToSection('approach')} className="nav-link text-sm">Our Approach</button>
              <button onClick={() => scrollToSection('who')} className="nav-link text-sm">Who We Work With</button>
              <button onClick={() => scrollToSection('about')} className="nav-link text-sm">About</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link text-sm">Speak to Us</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link text-sm">FAQs</button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#d9fb06] p-2">

              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen &&
          <div className="lg:hidden pb-6 pt-2 space-y-3">
              <button onClick={() => scrollToSection('why')} className="block w-full text-left py-2 text-[#d9fb06] hover:opacity-80 transition-opacity">Why Primal Response</button>
              <button onClick={() => scrollToSection('approach')} className="block w-full text-left py-2 text-[#d9fb06] hover:opacity-80 transition-opacity">Our Approach</button>
              <button onClick={() => scrollToSection('who')} className="block w-full text-left py-2 text-[#d9fb06] hover:opacity-80 transition-opacity">Who We Work With</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-[#d9fb06] hover:opacity-80 transition-opacity">About</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-[#d9fb06] hover:opacity-80 transition-opacity">Speak to Us</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left py-2 text-[#d9fb06] hover:opacity-80 transition-opacity">FAQs</button>
            </div>
          }
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section relative min-h-screen flex items-center pt-20">
        <div className="hero-background">
          <img
            src="https://customer-assets.emergentagent.com/job_survival-edge/artifacts/3htkbqqm_hero-background.jpg"
            alt="Survival Training"
            className="hero-image" />

          <div className="hero-overlay"></div>
        </div>
        
        <div className="container mx-auto px-5 md:px-10 relative z-10">
          <div className="hero-content max-w-2xl">
            <h1 className="hero-title mb-6">
              SURVIVAL IS BIOLOGICAL, NOT TACTICAL
            </h1>
            <p className="body-large mb-4 text-[#d9fb06]">
              Prepared for reality, not theory
            </p>
            <p className="body-medium max-w-xl !font-bold !text-center mb-8 !text-[#0D0D0D]">
              Primal Response provides specialist survival education for civilians, organisations and individuals who want to understand how the human body and mind respond when circumstances change.
            </p>
            <p className="body-small max-w-xl !font-bold !text-justify mb-12 !text-[#0D0D0D]">
              Our approach is grounded in biology and psychology. Survival is not about equipment, tactics or dramatic scenarios. It is about supporting human physiology, managing stress responses, and adapting quickly when environments, situations or expectations shift.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="btn-primary">

                SPEAK TO US ABOUT YOUR REQUIREMENTS
              </Button>
              <Button
                onClick={() => scrollToSection('approach')}
                className="btn-secondary">

                EXPLORE OUR APPROACH
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown size={32} className="text-[#d9fb06]" />
        </div>
      </section>

      {/* Biology Quote Section */}
      <section className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="body-large text-[#d9fb06] mb-6">
              At Primal Response, we believe that survival isn't about "toughness"—it is a biological process.
            </p>
            <p className="body-medium text-[#888680]">
              The human body is an incredible machine, but in moments of extreme stress, our natural physiological responses can either be our greatest asset or our biggest liability. Whether you are navigating the rural, the urban or the wilds, managing a critical situation such as a medical emergency, isolated in a remote location, or facing a threat on a city street, your success depends on how you manage your own biology.
            </p>
          </div>
        </div>
      </section>

      {/* Why Primal Response */}
      <section id="why" className="py-24 bg-[#1a1c1b]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-12">A DIFFERENT WAY TO THINK ABOUT SURVIVAL</h2>
            <p className="body-medium text-[#888680] mb-8">
              Primal Response approaches survival as a process, not an event.
            </p>
            <p className="body-medium text-[#888680] mb-8">
              To survive, the human organism must continue to function when normal conditions are disrupted. This depends on supporting the essentials of life — breathing, circulation, temperature, consciousness and injury management — while also managing fear, stress and perception.
            </p>
            <p className="body-medium text-[#d9fb06] mb-8">
              Most survival situations do not involve violence.
            </p>
            <p className="body-medium text-[#888680] mb-12">
              They involve accidents, medical emergencies, environmental exposure, confusion or delayed help. Our work focuses on simplicity, realism and adaptability under stress.
            </p>
          </div>
        </div>
      </section>

      {/* Foundations Section */}
      <section id="approach" className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-5 md:px-10">
          <h2 className="heading-2 text-[#d9fb06] mb-16 text-center">FOUNDATIONS OF SURVIVAL</h2>
          <p className="body-medium text-[#888680] mb-16 text-center max-w-3xl mx-auto">
            Primal Response is built on three foundations: Bushcraft, Medical, and Psychology. These foundations are not taught in isolation. They work together to support survival in real conditions.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Bushcraft Card */}
            <div className="team-card">
              <div className="team-card-image" style={{
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_survival-edge/artifacts/8bm2h67i_bushcraft.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              </div>
              <div className="team-card-content">
                <h3 className="heading-4 text-[#d9fb06] mb-4">BUSHCRAFT</h3>
                <p className="body-small text-[#888680] mb-4">Supporting the body within the environment</p>
                <p className="body-small text-[#888680]">
                  Bushcraft focuses on maintaining warmth, hydration, energy, shelter and movement. Environmental exposure is often the first factor to undermine survival. Supporting the body allows time for other decisions to matter.
                </p>
              </div>
            </div>

            {/* Medical Card */}
            <div className="team-card">
              <div className="team-card-image" style={{
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_survival-edge/artifacts/k0ca87a2_medical.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              </div>
              <div className="team-card-content">
                <h3 className="heading-4 text-[#d9fb06] mb-4">MEDICAL</h3>
                <p className="body-small text-[#888680] mb-4">Preserving life through simple early intervention</p>
                <p className="body-small text-[#888680]">
                  Most preventable deaths occur early and often involve airway compromise, bleeding, unconsciousness or exposure. Primal Response medical education focuses on simple actions that support physiology under stress.
                </p>
              </div>
            </div>

            {/* Psychology Card */}
            <div className="team-card">
              <div className="team-card-image" style={{
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_survival-edge/artifacts/6myq3yal_psychology_pe.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              </div>
              <div className="team-card-content">
                <h3 className="heading-4 text-[#d9fb06] mb-4">PSYCHOLOGY</h3>
                <p className="body-small text-[#888680] mb-4">Adapting quickly when circumstances change</p>
                <p className="body-small text-[#888680]">
                  Psychology underpins survival. Fear, stress and confusion are biological responses. Survival depends on recognising these responses quickly and adapting behaviour accordingly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Train */}
      <section className="py-24 bg-[#1a1c1b]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-12">HOW WE TRAIN</h2>
            
            <div className="space-y-12">
              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Simplicity Under Stress</h3>
                <p className="body-medium text-[#888680]">
                  When stress increases, memory narrows and fine motor skills degrade. Primal Response training prioritises simple actions that work reliably under pressure. If something cannot be done under stress, it is not considered a survival skill.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Supporting Human Physiology</h3>
                <p className="body-medium text-[#888680]">
                  Training focuses on keeping the body functioning through breathing, circulation, temperature, positioning and injury management. Understanding why things fail is as important as knowing what to do.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Psychological Adaptation</h3>
                <p className="body-medium text-[#888680]">
                  Training develops awareness of fear responses, stress effects and perception changes. The aim is not to remove fear, but to function despite it.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Bespoke by Design</h3>
                <p className="body-medium text-[#888680]">
                  There are no off-the-shelf courses. Training is shaped around the individual, group or organisation, the environment, likely risks and existing confidence.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Realism Without Theatre</h3>
                <p className="body-medium text-[#888680]">
                  Training reflects uncertainty, fatigue and delayed support. The aim is preparation that holds up in real conditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Psychology of Survival */}
      <section className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-12">THE PSYCHOLOGY OF SURVIVAL</h2>
            
            <div className="space-y-12">
              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Survival Begins in the Mind</h3>
                <p className="body-medium text-[#888680]">
                  Survival is decided by how quickly the brain and body adapt to change. Stress responses are automatic and biological. Recognising and managing them is key.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Fear and Risk Are Not the Same</h3>
                <p className="body-medium text-[#888680] mb-4">
                  Fear is a biological response. Risk is a probability.
                </p>
                <p className="body-medium text-[#888680]">
                  Modern life amplifies fear, while most survival situations arise from accidents, illness or environmental exposure. Understanding this distinction allows proportionate responses.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Adaptation Is the Key Skill</h3>
                <p className="body-medium text-[#888680]">
                  Survivors are not the strongest or most confident. They are the quickest to adapt. Training develops this capacity through understanding and exposure.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Confidence Through Understanding</h3>
                <p className="body-medium text-[#888680]">
                  Confidence grounded in understanding is resilient. Confidence based on assumption is fragile.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section id="who" className="py-24 bg-[#1a1c1b]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-12">WHO WE WORK WITH</h2>
            <p className="body-large text-[#d9fb06] mb-12">Civilians First</p>
            <p className="body-medium text-[#888680] mb-16">
              Primal Response works with civilians and civilian organisations. The focus is on realistic preparation for people with responsibility for themselves or others.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="p-8 border border-[#3f4816] hover:bg-[#302f2c] transition-colors">
                <h3 className="heading-5 text-[#d9fb06] mb-4">INDIVIDUALS</h3>
                <p className="body-small text-[#888680]">
                  People seeking understanding of how to respond to emergencies, manage fear and support life when help is delayed.
                </p>
              </div>

              <div className="p-8 border border-[#3f4816] hover:bg-[#302f2c] transition-colors">
                <h3 className="heading-5 text-[#d9fb06] mb-4">ORGANISATIONS</h3>
                <p className="body-small text-[#888680]">
                  Organisations with duty-of-care responsibilities seeking realistic, proportionate preparation for accidents, emergencies and major incidents.
                </p>
              </div>

              <div className="p-8 border border-[#3f4816] hover:bg-[#302f2c] transition-colors">
                <h3 className="heading-5 text-[#d9fb06] mb-4">GROUPS AND TEAMS</h3>
                <p className="body-small text-[#888680]">
                  Teams who need shared understanding and coordinated responses under pressure.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="body-medium text-[#d9fb06]">
                A Common Principle: Confidence must be earned through understanding, not assumed through instruction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-16 text-center">WHAT WE DO</h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8 bg-[#1a1c1b] border border-[#3f4816]">
                <h3 className="heading-5 text-[#d9fb06] mb-4">SURVIVAL & ENVIRONMENTAL PREPAREDNESS</h3>
                <p className="body-small text-[#888680]">
                  Preparation for exposure, movement, shelter, hydration and environmental stress.
                </p>
              </div>

              <div className="p-8 bg-[#1a1c1b] border border-[#3f4816]">
                <h3 className="heading-5 text-[#d9fb06] mb-4">MEDICAL SURVIVAL & LIFE PRESERVATION</h3>
                <p className="body-small text-[#888680]">
                  Physiology-led education focused on early intervention and simple life-preserving actions.
                </p>
              </div>

              <div className="p-8 bg-[#1a1c1b] border border-[#3f4816]">
                <h3 className="heading-5 text-[#d9fb06] mb-4">PSYCHOLOGICAL SURVIVAL & STRESS ADAPTATION</h3>
                <p className="body-small text-[#888680]">
                  Understanding fear, stress and perception to enable functional decision-making.
                </p>
              </div>

              <div className="p-8 bg-[#1a1c1b] border border-[#3f4816]">
                <h3 className="heading-5 text-[#d9fb06] mb-4">MAJOR INCIDENT & EMERGENCY PREPAREDNESS</h3>
                <p className="body-small text-[#888680]">
                  Preparation for rare but high-impact events with a focus on survival, avoidance and recovery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#1a1c1b]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12 p-8 bg-[#302f2c] border-l-4 border-[#d9fb06]">
              <p className="body-large text-[#888680] italic">
                "Survival is adaptation."
              </p>
              <p className="caption text-[#888680] mt-2">
                — Dr Eugene Porter, The Walking Dead
              </p>
            </div>

            <h2 className="heading-2 text-[#d9fb06] mb-12">ABOUT PRIMAL RESPONSE</h2>

            <div className="space-y-12">
              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Survival as Adaptation</h3>
                <p className="body-medium text-[#888680]">
                  Primal Response is founded on a simple principle: survival is not tactical, it is biological and psychological. To survive, humans must support their physiology and manage their psychological response as environments and circumstances change. When this happens early and simply, survival improves. When it does not, outcomes deteriorate quickly.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">The Origin of Primal Response</h3>
                <p className="body-medium text-[#888680]">
                  Primal Response began as a way of articulating how survival actually works. As demand grew from individuals and organisations seeking realistic preparation, it evolved into a company focused on personal resilience grounded in biology, psychology and lived experience rather than theory or performance. The emphasis has always been on simple actions done well, because under stress, complexity fails.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">The Founder</h3>
                <div className="grid md:grid-cols-3 gap-8 mb-6">
                  <div className="md:col-span-1">
                    <div className="aspect-square bg-[#302f2c] rounded-lg overflow-hidden">
                      <img
                        src="https://customer-assets.emergentagent.com/job_survival-edge/artifacts/o4qwfju7_DC.jpg"
                        alt="Dave Connell"
                        className="w-full h-full object-cover" />

                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <p className="body-medium text-[#888680] mb-4">
                      Primal Response is led by Dave Connell.
                    </p>
                    <p className="body-small text-[#888680] mb-4">
                      Dave discovered the outdoors through the Scouting movement before training as a medic in the British Army and later serving within the Medical Support Unit of UK Special Forces.
                    </p>
                    <p className="body-small text-[#888680]">
                      After leaving military service in 2004, he co-founded a medical life-support company operating in challenging environments including Afghanistan, Iraq, the Democratic Republic of Congo, Algeria, Sudan and Darfur.
                    </p>
                  </div>
                </div>
                <p className="body-small text-[#888680] mb-4">
                  Across a career spanning military and civilian contexts, Dave has worked in approximately 35% of the world's countries, developing a broad, practical understanding of risk, safety, pre-hospital care, survival and bushcraft.
                </p>
                <p className="body-small text-[#888680]">
                  This experience informs a calm, restrained approach to survival education that prioritises judgement, responsibility and realism.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">Film & Television Experience</h3>
                <p className="body-small text-[#888680] mb-4">
                  Dave's survival expertise has also been applied within film and television, where realism, adaptability and safety are essential. His work includes:
                </p>
                <ul className="body-small text-[#888680] space-y-2 list-disc list-inside">
                  <li>Last Man Standing – Papua New Guinea</li>
                  <li>10,000 BC – Bulgaria (Series 1 & 2)</li>
                  <li>Manhunt – Mongolia, New Zealand, Mexico, USA</li>
                  <li>Marooned with Ed Stafford – Philippines, Argentina, Botswana, Norway</li>
                  <li>Left for Dead with Ed Stafford – Mongolia, Bolivia, Laos</li>
                  <li>First Man Out – Thailand, Palau, Borneo, Mongolia, Kazakhstan, India</li>
                </ul>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">The Primal Response Approach</h3>
                <p className="body-medium text-[#888680] mb-4">
                  Primal Response does not teach identity or extremity. It teaches:
                </p>
                <ul className="body-medium text-[#d9fb06] space-y-2">
                  <li>• Understanding over instruction</li>
                  <li>• Simplicity over complexity</li>
                  <li>• Adaptation over assumption</li>
                </ul>
                <p className="body-medium text-[#888680] mt-6">
                  This is survival education designed for real people, in real environments, facing real uncertainty. Survival is not about who you are. It is about how quickly you adapt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-12">SPEAK TO US</h2>
            
            <div className="space-y-8 mb-12">
              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">A Conversation, Not a Booking</h3>
                <p className="body-medium text-[#888680]">
                  Primal Response does not offer off-the-shelf courses. Every session is shaped around the individual, group or organisation, their environment and what they want to be prepared for. The first step is simply a conversation to understand context and explore what would be most appropriate.
                </p>
              </div>

              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-4">What to Expect</h3>
                <p className="body-medium text-[#888680] mb-4">
                  When you get in touch, we'll ask a few simple questions to understand:
                </p>
                <ul className="body-small text-[#888680] space-y-2">
                  <li>• Who the training is for</li>
                  <li>• The environment or context</li>
                  <li>• What you want people to feel more confident about</li>
                  <li>• Any practical considerations such as location or timing</li>
                </ul>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-[#1a1c1b] p-8 border border-[#3f4816]">
              <div>
                <label className="body-small text-[#d9fb06] block mb-2">Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  className="bg-[#302f2c] border-[#3f4816] text-[#d9fb06]" />

              </div>

              <div>
                <label className="body-small text-[#d9fb06] block mb-2">Organisation (optional)</label>
                <Input
                  type="text"
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleFormChange}
                  className="bg-[#302f2c] border-[#3f4816] text-[#d9fb06]" />

              </div>

              <div>
                <label className="body-small text-[#d9fb06] block mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="bg-[#302f2c] border-[#3f4816] text-[#d9fb06]" />

              </div>

              <div>
                <label className="body-small text-[#d9fb06] block mb-2">Phone (optional)</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  className="bg-[#302f2c] border-[#3f4816] text-[#d9fb06]" />

              </div>

              <div>
                <label className="body-small text-[#d9fb06] block mb-2">Location (optional)</label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  className="bg-[#302f2c] border-[#3f4816] text-[#d9fb06]" />

              </div>

              <div>
                <label className="body-small text-[#d9fb06] block mb-2">Tell us about your context or what you'd like to explore *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  required
                  rows={6}
                  className="bg-[#302f2c] border-[#3f4816] text-[#d9fb06]" />

              </div>

              <Button type="submit" className="btn-primary w-full">
                SUBMIT ENQUIRY
              </Button>
            </form>

            <div className="mt-12">
              <h3 className="heading-5 text-[#d9fb06] mb-4">What Happens Next</h3>
              <p className="body-small text-[#888680]">
                Once we've reviewed your enquiry, we'll be in touch to clarify any questions, discuss options, and confirm whether Primal Response is the right fit. If it is, we'll outline a suggested approach. If it isn't, we'll say so.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-[#1a1c1b]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-[#d9fb06] mb-12">FREQUENTLY ASKED QUESTIONS</h2>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Who is Primal Response training for?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Primal Response works with civilians and civilian organisations. This includes individuals, groups and organisations who want realistic preparation for situations where conditions may be uncertain, unfamiliar or change quickly. We do not provide training for police or military units.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Do I need prior experience?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  No. Training is designed for people with no specialist background as well as those with existing experience. Sessions are shaped around the individual or group, their environment, and their confidence level. The focus is on understanding and simple actions that work under stress.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Is this training about violence or combat?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  No. Primal Response training is not tactical or combat-focused. Most survival situations involve accidents, medical emergencies, environmental exposure or confusion rather than deliberate violence. Where rare but high-impact events are discussed, the emphasis is on avoidance, survival, life preservation and recovery, not confrontation.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  How long is a typical session?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Most sessions are delivered as a half-day (approximately four hours). This allows time to explore concepts properly, practise simple actions and develop understanding without overload. Longer or shorter sessions can be discussed depending on need.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Where does the training take place?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Training can be delivered at your workplace or organisation's premises, at a suitable external venue, or at an agreed location appropriate to the context. Venue requirements depend on the nature of the session and will be discussed in advance to ensure suitability and safety.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Is the training flexible or bespoke?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Yes. All Primal Response training is bespoke. There are no off-the-shelf courses. Sessions are shaped around the individual or organisation, the environment and likely risks, existing experience and confidence, and practical relevance. The aim is preparation that fits the person and the situation.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  How many people can attend a session?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Sessions are typically designed for small groups to maintain quality and engagement. Group size depends on the session focus and venue. This will be discussed during the initial conversation to ensure the right balance between interaction and learning.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Is this practical or classroom-based?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Training combines discussion, explanation and practical demonstration where appropriate. The emphasis is on understanding and simple actions, rather than performance or complex drills. Everything is designed to be realistic and achievable under stress.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  What will participants gain from the session?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Participants typically leave with a clearer understanding of how the body and mind respond under stress, practical simple actions that support survival, increased confidence grounded in understanding, and improved ability to adapt when circumstances change. Confidence is developed through clarity, not fear.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  How do we book or enquire?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Primal Response begins with a conversation. If you would like to discuss your context, environment or requirements, please get in touch via the enquiry form. We'll talk through what would be most appropriate and take it from there.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-11" className="border border-[#3f4816] bg-[#302f2c] px-6">
                <AccordionTrigger className="text-[#d9fb06] hover:text-[#d9fb06]/80 text-left">
                  Is this suitable for organisations with duty-of-care responsibilities?
                </AccordionTrigger>
                <AccordionContent className="text-[#888680]">
                  Yes. Primal Response works with organisations seeking proportionate, realistic preparation for their people. Training is designed to be practical, responsible and relevant, rather than alarmist or excessive.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#302f2c] border-t border-[#3f4816]">
        <div className="container mx-auto px-5 md:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-[#d9fb06] font-bold text-xl mb-4">PRIMAL RESPONSE</h3>
                <p className="caption text-[#888680]">
                  Survival is biological, not tactical
                </p>
              </div>
              
              <div>
                <h4 className="text-[#d9fb06] font-semibold mb-4">Quick Links</h4>
                <div className="space-y-2">
                  <button onClick={() => scrollToSection('why')} className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Why Primal Response</button>
                  <button onClick={() => scrollToSection('approach')} className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Our Approach</button>
                  <button onClick={() => scrollToSection('who')} className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Who We Work With</button>
                  <button onClick={() => scrollToSection('about')} className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">About</button>
                </div>
              </div>

              <div>
                <h4 className="text-[#d9fb06] font-semibold mb-4">Get in Touch</h4>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="btn-primary text-sm">

                  CONTACT US
                </button>
              </div>
            </div>

            <div className="pt-8 border-t border-[#3f4816] text-center">
              <p className="caption text-[#888680]">
                © {new Date().getFullYear()} Primal Response. All rights reserved. | Prepared for reality, not theory.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;